import Dropdown from '@ubnt/ui-components/Dropdown/Dropdown';
import { DropdownBaseProps, DropdownProps } from '@ubnt/ui-components/Dropdown/types';
import { TooltipProps } from '@ubnt/ui-components/Tooltip/Tooltip';
import Tooltip from '@ubnt/link-core-ui/components/Tooltip';
import { clsx } from 'clsx';
import { memo, useMemo } from 'react';

import TooltipOption from 'components/ui-components/TooltipOption';

import classes from './ChannelDropdown.module.scss';

export interface ChannelDropdownProps extends DropdownBaseProps {
  channel: string;
  channels: {
    channel: string;
    disabled?: boolean;
    isDFS?: boolean;
    tooltipDescription?: TooltipProps['description'];
  }[];
  inputProps?: DropdownProps['inputProps'];
  isAuto?: boolean;
  tooltipDescription?: TooltipProps['description'];
}

export const AUTO_CHANNEL = 'AUTO_CHANNEL';
export const AUTO_CHANNEL_LABEL = 'Auto';

export type AUTO_CHANNEL_TYPE = typeof AUTO_CHANNEL;

const ChannelDropdown = (props: ChannelDropdownProps) => {
  // oxlint-disable-next-line @typescript-eslint/no-unused-vars
  const { channels, channel, disabled, inputProps, isAuto, tooltipDescription, ...rest } = props;

  const channelOptions = useMemo(
    () =>
      channels.map((channelValue) => {
        const { channel, isDFS, ...rest } = channelValue;
        return {
          value: channel,
          label: `${channel}${isDFS ? ' (DFS)' : ''}`,
          ...rest,
        };
      }),
    [channels]
  );

  const dropdownInputProps = useMemo(
    () => ({
      title: channel,
      ...inputProps,
    }),
    [channel, inputProps]
  );

  const dropdown = useMemo(
    () => (
      <Dropdown
        disabled={disabled}
        inputProps={dropdownInputProps}
        options={channelOptions}
        optionContainerClassName={classes.optionContainer}
        renderOption={TooltipOption}
        value={channel}
        variant="secondary"
        width="100%"
        optionsWidth="contentWidth"
        {...rest}
      />
    ),
    [disabled, dropdownInputProps, channelOptions, channel, rest]
  );

  if (tooltipDescription) {
    return (
      <Tooltip
        className={clsx(classes.tooltip, disabled && classes.disabledCursor)}
        description={tooltipDescription}
        portal
      >
        {dropdown}
      </Tooltip>
    );
  }

  return dropdown;
};

const MemoizedChannelDropdown = memo(ChannelDropdown);

export default MemoizedChannelDropdown;
